import type { ParseKeys } from 'i18next';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Button';
import { CustomPeriodCard } from '@/pages/Overview/CustomPeriodCard';
import { EvaluationTypePicker } from '@/pages/Overview/EvaluationTypePicker';
import { RatingsEmptyCard } from '@/pages/Overview/RatingsEmptyCard';
import { RatingsPaywall } from '@/pages/Overview/RatingsPaywall';
import { RatingsResults } from '@/pages/Overview/RatingsResults';
import { formatPeriodRange, isSamePeriod } from '@/pages/Overview/period';
import { useCalendarOverflowSpace } from '@/pages/Overview/hooks/useCalendarOverflowSpace';
import { useSelectedPeriod } from '@/pages/Overview/hooks/useSelectedPeriod';
import { RatingsOptionCard } from '@/pages/Overview/RatingsOptionCard';
import { SameLevelClassChips } from '@/pages/Overview/SameLevelClassChips';
import { useHasFamilySubscription } from '@/hooks/useHasFamilySubscription';
import { useShowSubscriptionOffer } from '@/hooks/useShowSubscriptionOffer';
import { ButtonVariant, EvaluationMode, Size } from '@/types/components';
import { StudyYearPeriodType } from '@/types/overview';

const COMPARE_OPTIONS: Array<{
  includeSameLevelClasses: boolean;
  labelKey: ParseKeys;
}> = [
  {
    includeSameLevelClasses: false,
    labelKey: 'overview.ratings.compareClassmates',
  },
  {
    includeSameLevelClasses: true,
    labelKey: 'overview.ratings.compareParallel',
  },
];

export const Ratings: React.FC = () => {
  const { t } = useTranslation();
  const hasSubscription = useHasFamilySubscription();
  const showSubscriptionOffer = useShowSubscriptionOffer();
  const [evaluationMode, setEvaluationMode] = useState(EvaluationMode.Average);
  const [selectedLessonTypeId, setSelectedLessonTypeId] = useState<
    number | null
  >(null);
  const { periods, studyYearPeriod, selectedPeriod, selectPeriod } =
    useSelectedPeriod();
  const [includeSameLevelClasses, setIncludeSameLevelClasses] = useState(false);
  const [sameLevelClassIds, setSameLevelClassIds] = useState<number[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const { contentEndRef, calendarSpace, handleCalendarBottomChange } =
    useCalendarOverflowSpace();

  const isCustomPeriod = selectedPeriod.type === StudyYearPeriodType.Custom;

  const handleEvaluationChange = (
    mode: EvaluationMode,
    lessonTypeId: number | null,
  ) => {
    setEvaluationMode(mode);
    setSelectedLessonTypeId(lessonTypeId);
  };

  if (showSubscriptionOffer) {
    return <RatingsPaywall />;
  }

  if (!hasSubscription) {
    return (
      <div className="flex flex-1 flex-col pt-1 md:px-6 md:py-6">
        <RatingsEmptyCard />
      </div>
    );
  }

  if (isLoaded) {
    return (
      <RatingsResults
        filters={{
          period: selectedPeriod,
          includeSameLevelClasses,
          sameLevelClassIds,
          lessonTypeId: selectedLessonTypeId,
        }}
        evaluationMode={evaluationMode}
        onEvaluationChange={handleEvaluationChange}
        onPeriodChange={selectPeriod}
        onCompareChange={setIncludeSameLevelClasses}
        onSameLevelClassesChange={setSameLevelClassIds}
      />
    );
  }

  return (
    <div className="flex flex-1 flex-col pt-1 md:flex-none md:px-6 md:py-6">
      <div className="flex flex-1 flex-col gap-4 rounded-t-2xl bg-popover pt-4 pb-8 md:flex-none md:gap-8 md:rounded-[20px] md:bg-card md:py-6">
        <div className="flex flex-col gap-1 px-5">
          <h1 className="text-[16px]/[24px] font-medium text-foreground md:text-[24px]/[32px] md:font-semibold">
            {t('overview.tabs.ratings')}
          </h1>
          <p className="text-body-14 text-muted-foreground">
            {t('overview.ratings.subtitle')}
          </p>
        </div>

        <section className="mt-1 flex flex-col gap-4 px-5 md:mt-0">
          <h2 className="hidden text-callout-16 text-foreground md:block">
            {t('overview.ratings.evaluationType')}
          </h2>
          <EvaluationTypePicker
            mode={evaluationMode}
            lessonTypeId={selectedLessonTypeId}
            onChange={handleEvaluationChange}
          />
        </section>

        <section className="flex flex-col gap-2 px-5 md:gap-4">
          <h2 className="text-subhead-14 text-muted-foreground md:text-callout-16 md:text-foreground">
            {t('overview.ratings.period')}
          </h2>
          <div className="grid items-start gap-2 md:grid-cols-2">
            {periods.map((period) => (
              <RatingsOptionCard
                key={period.name}
                title={period.name}
                subtitle={formatPeriodRange(period)}
                hasIcon
                isSelected={isSamePeriod(period, selectedPeriod)}
                onSelect={() => selectPeriod(period)}
              />
            ))}
            <CustomPeriodCard
              studyYearPeriod={studyYearPeriod}
              value={isCustomPeriod ? selectedPeriod : null}
              onChange={selectPeriod}
              onCalendarBottomChange={handleCalendarBottomChange}
            />
          </div>
        </section>

        <section className="flex flex-col gap-2 px-5 md:gap-4">
          <h2 className="text-subhead-14 text-muted-foreground md:text-callout-16 md:text-foreground">
            {t('overview.ratings.compare')}
          </h2>
          <div className="flex flex-col gap-2 md:w-[calc((100%-8px)/2)]">
            {COMPARE_OPTIONS.map((option) => (
              <RatingsOptionCard
                key={option.labelKey}
                title={t(option.labelKey)}
                isSelected={
                  option.includeSameLevelClasses === includeSameLevelClasses
                }
                onSelect={() =>
                  setIncludeSameLevelClasses(option.includeSameLevelClasses)
                }
              />
            ))}
            <SameLevelClassChips
              isVisible={includeSameLevelClasses}
              className="-mt-2"
              value={sameLevelClassIds}
              onChange={setSameLevelClassIds}
            />
          </div>
        </section>

        <div className="px-5">
          <Button
            variant={ButtonVariant.Primary}
            size={Size.Medium}
            className="h-12 w-full font-semibold md:h-10 md:w-auto"
            onClick={() => setIsLoaded(true)}
          >
            {t('overview.ratings.load')}
          </Button>
          <div
            ref={contentEndRef}
            style={{ height: calendarSpace }}
            className="transition-[height] duration-300 ease-out"
          />
        </div>
      </div>
    </div>
  );
};
